/**
 * Transaction-level debt helpers (Swift Transaction's debt extension): the scheduled
 * balance and the annuity payment. The payoff date is DebtProjector's (debt-projector.ts, R15).
 */
import { monthsBetween, type Day } from '../core/calendar.ts';
import type { Decimal } from '../core/decimal.ts';
import { monthlyPayment, scheduledBalance } from '../calculations/loan-math.ts';
import type { Transaction } from '../types/domain.ts';

/** Original-loan mode reads the principal; any other mode (or none) the current balance. */
export const effectiveDebtPrincipal = (t: Transaction): Decimal | null =>
  t.debtEntryMode === 'originalLoan' ? t.debtPrincipal : t.debtCurrentBalance;

export const effectiveDebtTermMonths = (t: Transaction): number | null =>
  t.debtEntryMode === 'originalLoan' ? t.debtTermMonths : t.debtRemainingMonths;

const debtStart = (t: Transaction): Day => t.debtStartDate ?? t.startDate;

/** Annuity payment for a debt with a principal, rate and term; null otherwise. */
export function debtMonthlyPayment(t: Transaction): Decimal | null {
  const principal = effectiveDebtPrincipal(t);
  const termMonths = effectiveDebtTermMonths(t);
  if (t.type !== 'debt' || principal === null || t.interestRate === null || termMonths === null) return null;
  return monthlyPayment({
    principal,
    annualRatePercent: t.interestRate,
    termMonths,
    interestOnly: t.debtIsInterestOnly === true,
  });
}

/**
 * Balance at `date`: open-ended debts report the stored balance; fixed-term ones follow the
 * standard schedule from the debt start, falling back to a stored balance without full terms.
 */
export function debtBalance(t: Transaction, date: Day): Decimal | null {
  if (t.type !== 'debt') return null;
  if (t.debtType === 'openEnded') return t.debtCurrentBalance;
  const principal = effectiveDebtPrincipal(t);
  const termMonths = effectiveDebtTermMonths(t);
  if (principal === null || t.interestRate === null || termMonths === null) return t.debtCurrentBalance ?? t.debtPrincipal;
  return scheduledBalance({
    principal,
    annualRatePercent: t.interestRate,
    termMonths,
    paymentsMade: monthsBetween(debtStart(t), date),
    interestOnly: t.debtIsInterestOnly === true,
  });
}


